import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  FiMenu, 
  FiX, 
  FiShield, 
  FiExternalLink, 
  FiGithub, 
  FiTwitter, 
  FiLinkedin 
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  // Close menu on route change
  React.useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Product', href: 'https://peridot.company/product' },
    { name: 'IT Services', href: 'https://peridot.company/it-services' },
    { name: 'Use Cases', href: 'https://peridot.company/use-cases' },
    { name: 'Pricing', href: 'https://peridot.company/pricing' },
  ];

  const socialLinks = [
    { icon: FiGithub, href: 'https://peridot-sh.github.io/peridot/', label: 'GitHub' },
    { icon: FiTwitter, href: 'https://x.com/PeridotDev', label: 'Twitter' },
    { icon: FiLinkedin, href: 'https://www.linkedin.com/company/peridotcompany/', label: 'LinkedIn' },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans">
      {/* Top Utility Bar (Documentation) */}
      <div className="bg-slate-900 text-white py-2 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          <a 
            href="https://peridot.company/docs/getting-started/overview" 
            className="text-[11px] uppercase tracking-widest font-bold text-slate-400 hover:text-peridot-400 transition-colors flex items-center gap-1.5"
            target="_blank"
            rel="noopener noreferrer"
          >
            Documentation <FiExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Main Corporate Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo Group */}
            <div className="flex items-center gap-8">
              <a href="https://peridot.company" className="flex items-center gap-2.5 group">
                <div className="bg-slate-950 p-2 rounded-lg group-hover:bg-peridot-600 transition-all duration-300 shadow-sm">
                  <FiShield className="text-white" size={22} />
                </div>
                <span className="font-black text-2xl tracking-tighter text-slate-950">Peridot</span>
              </a>

              {/* Desktop Main Nav */}
              <nav className="hidden lg:flex items-center gap-7">
                {navLinks.map((link) => (
                  <a 
                    key={link.name} 
                    href={link.href} 
                    className="text-sm font-semibold text-slate-600 hover:text-slate-950 transition-colors"
                  >
                    {link.name}
                  </a>
                ))}
                <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>
                <Link 
                  to="/shadow-ai-benchmark-report-2026" 
                  className={`text-sm font-bold transition-colors ${
                    location.pathname.includes('benchmark') 
                      ? 'text-peridot-600' 
                      : 'text-slate-600 hover:text-slate-950'
                  }`}
                >
                  Report Home
                </Link>
              </nav>
            </div>
            
            {/* Action Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/ai-governance-audit" 
                className="bg-slate-950 hover:bg-slate-800 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all shadow-lg shadow-slate-200 active:scale-95"
              >
                Request Audit
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button 
              className="lg:hidden p-2.5 text-slate-900 bg-slate-50 rounded-lg border border-slate-200" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden fixed inset-0 top-[112px] z-40 bg-white border-t border-slate-100 p-6 overflow-y-auto"
          >
            <nav className="flex flex-col gap-6">
              <div className="space-y-4">
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Platform</p>
                {navLinks.map((link) => (
                  <a 
                    key={link.name} 
                    href={link.href} 
                    className="block text-xl font-bold text-slate-900 border-b border-slate-50 pb-2"
                  >
                    {link.name}
                  </a>
                ))}
              </div>
              
              <div className="space-y-4 pt-4">
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Research</p>
                <Link 
                  to="/shadow-ai-benchmark-report-2026" 
                  className="block text-xl font-bold text-peridot-600"
                >
                  Report Home
                </Link>
              </div>

              <div className="pt-8">
                <Link 
                  to="/ai-governance-audit" 
                  className="flex items-center justify-center w-full bg-slate-950 text-white px-5 py-4 rounded-xl text-lg font-bold shadow-xl"
                >
                  Request AI Audit
                </Link>
                <a 
                  href="https://peridot.company/docs/getting-started/overview"
                  className="flex items-center justify-center gap-2 mt-6 text-slate-500 font-semibold"
                >
                  Documentation <FiExternalLink size={14} />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-lg border-t border-slate-200 z-50">
        <Link to="/ai-governance-audit" className="flex items-center justify-center w-full bg-peridot-600 text-white px-5 py-4 rounded-xl font-black text-sm uppercase tracking-widest shadow-xl active:scale-95 transition-transform">
          Start Free AI Audit
        </Link>
      </div>

      {/* Corporate Footer */}
      <footer className="bg-slate-950 text-slate-400 py-20 pb-32 md:pb-20 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 mb-16">
            
            {/* Logo & Socials */}
            <div className="col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <FiShield size={28} className="text-peridot-500" />
                <span className="font-bold text-2xl text-white tracking-tighter">Peridot</span>
              </div>
              <p className="max-w-xs text-sm leading-relaxed text-slate-500 mb-8">
                The enterprise standard for Shadow AI discovery and governance. Secure your AI adoption without slowing down the business.
              </p>
              <div className="flex items-center gap-5">
                {socialLinks.map((social) => (
                  <a 
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-lg bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-slate-800 hover:border-slate-700"
                    aria-label={social.label}
                  >
                    <social.icon size={20} />
                  </a>
                ))}
              </div>
            </div>
            
            {/* Platform Links */}
            <div>
              <h4 className="text-white font-bold mb-6 text-xs uppercase tracking-widest">Platform</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="https://peridot.company/product" className="hover:text-peridot-400 transition-colors">Product Overview</a></li>
                <li><a href="https://peridot.company/it-services" className="hover:text-peridot-400 transition-colors">IT Services</a></li>
                <li><a href="https://peridot.company/use-cases" className="hover:text-peridot-400 transition-colors">Use Cases</a></li>
                <li><a href="https://peridot.company/pricing" className="hover:text-peridot-400 transition-colors">Pricing</a></li>
              </ul>
            </div>

            {/* Research & Resources */}
            <div>
              <h4 className="text-white font-bold mb-6 text-xs uppercase tracking-widest">Resources</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/shadow-ai-benchmark-report-2026" className="hover:text-peridot-400 transition-colors text-peridot-500/90">2026 Benchmark Report</Link></li>
                <li><a href="https://peridot.company/docs/getting-started/overview" className="hover:text-peridot-400 transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-peridot-400 transition-colors">Security Center</a></li>
                <li><a href="#" className="hover:text-peridot-400 transition-colors">Status</a></li>
              </ul>
            </div>

            {/* Support/Contact */}
            <div>
              <h4 className="text-white font-bold mb-6 text-xs uppercase tracking-widest">Support</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/ai-governance-audit" className="hover:text-peridot-400 transition-colors">Request Audit</Link></li>
                <li><a href="#" className="hover:text-peridot-400 transition-colors">Contact Sales</a></li>
                <li><a href="#" className="hover:text-peridot-400 transition-colors">Help Center</a></li>
              </ul>
            </div>
          </div>

          {/* Legal Bottom */}
          <div className="pt-10 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
              <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider">
                © 2026 Peridot Inc. All rights reserved.
              </p>
              <div className="flex gap-6 text-[11px] font-bold uppercase tracking-widest text-slate-600">
                <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
            <p className="text-[10px] text-slate-700 italic">
              Benchmark data based on n=400 global enterprise survey participants.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}